import { sizes } from '@dixa/lib-shared-tokens';
import * as Popover from '@radix-ui/react-popover';
import { PropsWithChildren } from 'lib-shared-types-utils';
import { useCallback, useEffect, useState } from 'react';
import { useDebounce } from 'react-use';

import { conversationsApi } from 'apis/conversations';
import { ConversationMetadata } from 'components/conversation/conversationMetadata';

import styles from './conversationChipPopover.module.scss';

type ConversationChipPopoverProps = {
  csid: number;
} & Popover.PopoverContentProps &
  PropsWithChildren;

export const ConversationChipPopover = ({
  csid,
  children,
  ...popoverContentProps
}: ConversationChipPopoverProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  useDebounce(() => setIsOpen(isHovered), isHovered ? 500 : 200, [isHovered]);

  const [readConversation, { conversation, isUninitialized, isLoading }] =
    conversationsApi.useLazyReadConversationQuery({
      selectFromResult: ({ data, ...rest }) => ({
        conversation: data?.data?.history.main_conversation,
        ...rest,
      }),
    });

  useEffect(() => {
    if (isOpen) {
      readConversation({ csid }, true);
    }
  }, [isOpen, csid, readConversation]);

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);

  return (
    <Popover.Root open={isOpen} onOpenChange={setIsOpen}>
      <Popover.Anchor
        asChild
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </Popover.Anchor>

      <Popover.Portal>
        <Popover.Content
          className={styles.popover}
          side="right"
          align="center"
          sideOffset={parseInt(sizes.spacer.values[100])}
          onOpenAutoFocus={(e) => e.preventDefault()}
          {...popoverContentProps}
          // React events bubble through portals to the consumer's tree (e.g. a wrapping link).
          onClick={(e) => {
            popoverContentProps.onClick?.(e);
            e.stopPropagation();
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {conversation ? (
            <ConversationMetadata
              csid={conversation.id}
              channel={conversation.initial_channel}
              direction={conversation.direction}
              status={conversation.status}
              isAbandoned={conversation.is_abandoned}
              queueName={conversation.queue?.name}
              createdAt={conversation.created_at}
              closedAt={conversation.closed_at}
              attributes={conversation.attributes}
            />
          ) : isUninitialized || isLoading ? (
            <ConversationMetadata isLoading />
          ) : null}

          <Popover.Arrow asChild width={16} height={8}>
            <svg className={styles.arrow} viewBox="0 0 16 8">
              <path d="M0 0 L8 8 L16 0" />
            </svg>
          </Popover.Arrow>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
